
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DollarSign } from 'lucide-react'
import { useState, useEffect } from 'react'
import { formatIDR } from '@/lib/currency'
import { useCreateInvoice } from '../hooks/useBilling'
import type { Tenant } from '@/lib/types'

interface CreateInvoiceModalProps {
  isOpen: boolean
  onClose: () => void
  tenants: Tenant[]
}

export function CreateInvoiceModal({ isOpen, onClose, tenants }: CreateInvoiceModalProps) {
  const { mutate: createInvoice, isPending: isCreating } = useCreateInvoice()
  
  const [formData, setFormData] = useState({
    tenantId: '',
    month: new Date().toISOString().slice(0, 7),
    amountCents: '',
    currency: 'IDR',
    dueDate: '',
  })

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        tenantId: '',
        month: new Date().toISOString().slice(0, 7),
        amountCents: '',
        currency: 'IDR',
        dueDate: '',
      })
    }
  }, [isOpen])

  const handleCreateInvoice = () => {
    if (!formData.tenantId || !formData.amountCents || !formData.month) return

    createInvoice({
      tenantId: parseInt(formData.tenantId),
      month: formData.month,
      amountCents: parseInt(formData.amountCents) * 100,
      currency: formData.currency,
      dueDate: formData.dueDate || undefined,
    }, {
      onSuccess: () => {
        onClose()
      }
    })
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Create New Invoice</CardTitle>
          <CardDescription>
            Generate a manual invoice for a tenant
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Tenant *</Label>
            <Select
              value={formData.tenantId}
              onValueChange={(value) => setFormData({ ...formData, tenantId: value })}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a tenant" />
              </SelectTrigger>
              <SelectContent>
                {tenants.map((tenant) => (
                  <SelectItem key={tenant.id} value={tenant.id.toString()}>
                    {tenant.name} {tenant.domain && `(${tenant.domain})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Month *</Label>
            <Input
              type="month"
              value={formData.month}
              onChange={(e) => setFormData({ ...formData, month: e.target.value })}
              required
            />
          </div>
          <div className="space-y-2">
            <Label>Amount (IDR) *</Label>
            <Input
              type="number"
              value={formData.amountCents}
              onChange={(e) => setFormData({ ...formData, amountCents: e.target.value })}
              placeholder="1000000"
              required
            />
            <p className="text-xs text-muted-foreground">
              {formData.amountCents && `= ${formatIDR(parseInt(formData.amountCents))}`}
            </p>
          </div>
          <div className="space-y-2">
            <Label>Due Date</Label>
            <Input
              type="date"
              value={formData.dueDate}
              onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
            />
            <p className="text-xs text-muted-foreground">Leave empty for net-30</p>
          </div>
          <div className="flex gap-2 pt-4">
            <Button onClick={handleCreateInvoice} disabled={isCreating} className="gap-2">
              <DollarSign className="h-4 w-4" />
              {isCreating ? 'Creating...' : 'Create Invoice'}
            </Button>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
